import {
  Modal,
  NewButton,
  NewButtonVariant,
} from "@visiblethread/react-components";
import { UserPermissionRow } from "components/admin";
import { CLOUD_ADMIN_LABEL, ON_PREM_ADMIN_LABEL } from "constants/systemAdmin";
import { modalTypes } from "constants/types";
import PropTypes from "prop-types";
import React, { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { hideModal } from "slices/app/actions/modals";
import { selectIsCloudMode } from "slices/app/selectors";
import { editUserPermissions } from "slices/systemAdmin/actions/systemAdmin";

const EditUserPermissionsModal = ({
  workspaceId,
  username,
  licensed,
  dictionary,
  readabilitySettings,
  acronymSettings,
  discoverySettings,
  excelOutputs,
  proposalStages,
  docStyleTemplates,
  editCurrencies,
  archiveStatuses,
  userAdmin,
}) => {
  const dispatch = useDispatch();

  const isCloudMode = useSelector(selectIsCloudMode);

  const [currentLicensed, setCurrentLicensed] = useState(licensed);
  const [currentDictionary, setCurrentDictionary] = useState(dictionary);
  const [currentReadabilitySettings, setCurrentReadabilitySettings] =
    useState(readabilitySettings);
  const [currentAcronymSettings, setCurrentAcronymSettings] =
    useState(acronymSettings);
  const [currentDiscoverySettings, setCurrentDiscoverySettings] =
    useState(discoverySettings);
  const [currentExcelOutputs, setCurrentExcelOutputs] = useState(excelOutputs);
  const [currentProposalStages, setCurrentProposalStages] =
    useState(proposalStages);
  const [currentDocStyleTemplates, setCurrentDocStyleTemplates] =
    useState(docStyleTemplates);
  const [currentEditCurrencies, setCurrentEditCurrencies] =
    useState(editCurrencies);
  const [currentArchiveStatuses, setCurrentArchiveStatuses] =
    useState(archiveStatuses);
  const [currentUserAdmin, setCurrentUserAdmin] = useState(userAdmin);

  const handleSave = () => {
    dispatch(
      editUserPermissions(
        workspaceId,
        username,
        currentLicensed,
        currentDictionary,
        currentReadabilitySettings,
        currentAcronymSettings,
        currentDiscoverySettings,
        currentExcelOutputs,
        currentProposalStages,
        currentDocStyleTemplates,
        currentEditCurrencies,
        currentArchiveStatuses,
        currentUserAdmin
      )
    );
  };

  const handleCancel = () => {
    dispatch(hideModal(modalTypes.EDIT_PERMISSIONS));
  };

  const handleLicensedChange = () => {
    if (currentLicensed) {
      setCurrentDictionary(false);
      setCurrentReadabilitySettings(false);
      setCurrentAcronymSettings(false);
      setCurrentDiscoverySettings(false);
      setCurrentExcelOutputs(false);
      setCurrentProposalStages(false);
      setCurrentDocStyleTemplates(false);
      setCurrentEditCurrencies(false);
      setCurrentArchiveStatuses(false);
    }
    setCurrentLicensed(!currentLicensed);
  };

  return (
    <Modal
      data-testid="editUserPermissionsModal"
      className="fade-in"
      show
      onDismiss={handleCancel}
      backdrop
      centered
    >
      <Modal.Header>
        <b className="edit__permissions__modal__header">Edit Permissions</b>
      </Modal.Header>
      <Modal.Body>
        <div className="edit__permissions__modal">
          <UserPermissionRow
            heading
            checked={currentLicensed}
            disabled={false}
            id="edit-permissions-licensed"
            onChange={handleLicensedChange}
            text="Is Licensed User"
            tooltipText='Making a user "Licensed User" allows them sign in and work with documents. Only "Licensed Users" use a license'
          />
          <UserPermissionRow
            checked={currentDictionary}
            disabled={!currentLicensed}
            id="edit-permissions-dictionaries"
            onChange={() => setCurrentDictionary(!currentDictionary)}
            text="Can Edit Dictionaries"
            tooltipText="Enabling this permission allows this user edit the Dictionaries for this workspace"
          />
          <UserPermissionRow
            checked={currentReadabilitySettings}
            disabled={!currentLicensed}
            id="edit-permissions-readability-settings"
            onChange={() =>
              setCurrentReadabilitySettings(!currentReadabilitySettings)
            }
            text="Can Edit Readability Settings"
            tooltipText="Enabling this permission allows this user edit the Readability Settings for this workspace"
          />
          <UserPermissionRow
            checked={currentAcronymSettings}
            disabled={!currentLicensed}
            id="edit-permissions-acronym-settings"
            onChange={() => setCurrentAcronymSettings(!currentAcronymSettings)}
            text="Can Edit Acronym Settings"
            tooltipText="Enabling this permission allows this user edit the Acronym Settings for this workspace"
          />
          <UserPermissionRow
            checked={currentDiscoverySettings}
            disabled={!currentLicensed}
            id="edit-permissions-discovery-settings"
            onChange={() =>
              setCurrentDiscoverySettings(!currentDiscoverySettings)
            }
            text="Can Edit Discovery Settings"
            tooltipText="Enabling this permission allows this user edit the Discovery Settings for this workspace"
          />
          <UserPermissionRow
            checked={currentExcelOutputs}
            disabled={!currentLicensed}
            id="edit-permissions-excel-outputs"
            onChange={() => setCurrentExcelOutputs(!currentExcelOutputs)}
            text="Can Edit Excel Outputs"
            tooltipText="Enabling this permission allows this user edit the Excel Outputs for this workspace"
          />
          <UserPermissionRow
            checked={currentProposalStages}
            disabled={!currentLicensed}
            id="edit-permissions-proposal-stages"
            onChange={() => setCurrentProposalStages(!currentProposalStages)}
            text="Can Edit Pipeline Stages"
            tooltipText="Enabling this permission allows this user edit the Pipeline Stages for this workspace"
          />
          <UserPermissionRow
            checked={currentDocStyleTemplates}
            disabled={!currentLicensed}
            id="edit-permissions-templates"
            onChange={() =>
              setCurrentDocStyleTemplates(!currentDocStyleTemplates)
            }
            text="Can Edit Style Templates"
            tooltipText="Enabling this permission allows this user edit the Templates for this workspace"
          />
          <UserPermissionRow
            checked={currentEditCurrencies}
            disabled={!currentLicensed}
            id="edit-permissions-currencies"
            onChange={() => setCurrentEditCurrencies(!currentEditCurrencies)}
            text="Can Edit Currencies"
            tooltipText="Enabling this permission allows this user edit the Currencies for this workspace"
          />
          <UserPermissionRow
            checked={currentArchiveStatuses}
            disabled={!currentLicensed}
            id="edit-permissions-archive-statuses"
            onChange={() => setCurrentArchiveStatuses(!currentArchiveStatuses)}
            text="Edit Archive Statuses"
            tooltipText="Enabling this permission allows this user edit the Archive Statuses for this workspace"
          />
          <UserPermissionRow
            heading
            checked={currentUserAdmin}
            disabled={false}
            id="edit-permissions-user-admin"
            onChange={() => setCurrentUserAdmin(!currentUserAdmin)}
            text={isCloudMode ? CLOUD_ADMIN_LABEL : ON_PREM_ADMIN_LABEL}
            tooltipText="This allows the user create modify and delete users within the workspace"
          />
        </div>
      </Modal.Body>
      <Modal.Footer>
        <NewButton
          data-test="cancelButton"
          variant={NewButtonVariant.l2}
          onClick={handleCancel}
        >
          Cancel
        </NewButton>

        <NewButton
          data-test="saveButton"
          variant={NewButtonVariant.l1}
          onClick={handleSave}
          disabled={false}
        >
          Save
        </NewButton>
      </Modal.Footer>
    </Modal>
  );
};

export default EditUserPermissionsModal;

EditUserPermissionsModal.propTypes = {
  workspaceId: PropTypes.number.isRequired,
  username: PropTypes.string.isRequired,
  licensed: PropTypes.bool.isRequired,
  dictionary: PropTypes.bool.isRequired,
  readabilitySettings: PropTypes.bool.isRequired,
  acronymSettings: PropTypes.bool.isRequired,
  discoverySettings: PropTypes.bool.isRequired,
  excelOutputs: PropTypes.bool.isRequired,
  proposalStages: PropTypes.bool.isRequired,
  docStyleTemplates: PropTypes.bool.isRequired,
  editCurrencies: PropTypes.bool.isRequired,
  archiveStatuses: PropTypes.bool.isRequired,
  userAdmin: PropTypes.bool.isRequired,
};
